import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GENERAL_CHECKS, SCHEMES, ROOM_TYPES, DAMP_SEVERITY_LEVELS, MIN_PHOTOS_PER_MEASURE, PROPERTY_TYPES, WALL_TYPES, DAMP_TYPES } from "@/lib/constants";
import { PhotoField } from "@/components/PhotoField";
import { PropertyMap } from "@/components/PropertyMap";
import { SignaturePad } from "@/components/SignaturePad";
import { FloorPlanBuilder } from "@/components/FloorPlanBuilder";
import { render2DSnapshot, render3DSnapshot, uploadDataUrl } from "@/lib/floorplan";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Save, CheckCircle2, Camera, Plus, Trash2, DoorOpen, AlertTriangle, Home, Droplets, Ruler, Wrench, Box, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const YNNA = ["Yes", "No", "N/A"];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

// Loft-access questions shown only when the relevant plant is located in the loft
const LOFT_GATES = {
  PV: {
    gate: "Inverter location", value: "Loft space",
    loftQuestions: ["Boarded walkway / raised boarding provided for access to the loft inverter?", "Fire / smoke alarm installed in the loft space?"],
    elsewhereQuestions: ["Where is the inverter located?"],
  },
  ASHP: {
    gate: "Hot water cylinder location", value: "Loft space",
    loftQuestions: ["Boarded walkway / raised boarding provided for access to the loft cylinder?"],
    elsewhereQuestions: ["Where is the hot water cylinder located?"],
  },
};

// A whole question section shown only when a controlling question has the gate value
const SECTION_GATES = {
  PV: { section: "Battery storage", gate: "Battery storage installed as part of this PV system?", value: true },
};


export default function VisitForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [measures, setMeasures] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    visit_type: params.get("type") || "mid",
    client_scheme: "Saffron Housing",
    job_reference: "",
    completed_by: "",
    completed_by_role: "Retrofit Coordinator",
    visit_date: new Date().toISOString().slice(0, 10),
    visit_time: new Date().toTimeString().slice(0, 5),
    job_address: "",
    postcode: "",
    property_type: "",
    wall_type: "",
    risk_path: "",
    resident_present: false,
    resident_name: "",
    front_photo: null,
    general: {},
    general_confirmed: false,
    general_notes: {},
    overall_notes: "",
    engineer_signature: null,
    resident_signature: null,
    client_satisfied: false,
    client_comments: "",
    client_signature: null,
    floor_plan: null,
    damp_report: {},
    status: "draft",
  });
  const [mState, setMState] = useState({});
  const [actions, setActions] = useState([]);
  const [dampEntries, setDampEntries] = useState([]);
  const [remediations, setRemediations] = useState([]);
  const [pendingScheme, setPendingScheme] = useState(null);

  useEffect(() => {
    async function boot() {
      const mRes = await api.get("/measures");
      setMeasures(mRes.data.filter((m) => m.active));
      api.get("/installers").then((r) => setInstallers(r.data)).catch(() => {});

      if (editing) {
        const { data: v } = await api.get(`/visits/${id}`);
        setForm({
          visit_type: v.visit_type,
          client_scheme: v.client_scheme,
          job_reference: v.job_reference || "",
          completed_by: v.completed_by || "",
          completed_by_role: v.completed_by_role || "",
          visit_date: v.visit_date,
          visit_time: v.visit_time || "",
          job_address: v.job_address || "",
          postcode: v.postcode || "",
          property_type: v.property_type || "",
          wall_type: v.wall_type || "",
          risk_path: v.risk_path || "",
          resident_present: v.resident_present || false,
          resident_name: v.resident_name || "",
          front_photo: v.front_photo || null,
          general: v.general || {},
          general_confirmed: v.general_confirmed || false,
          general_notes: v.general_notes || {},
          overall_notes: v.overall_notes || "",
          engineer_signature: v.engineer_signature || null,
          resident_signature: v.resident_signature || null,
          client_satisfied: v.client_satisfied || false,
          client_comments: v.client_comments || "",
          client_signature: v.client_signature || null,
          floor_plan: v.floor_plan || null,
          damp_report: v.damp_report || {},
          status: v.status || "draft",
        });
        const ms = {};
        (v.measures || []).forEach((m) => {
          ms[m.measure_id] = {
            installed: m.installed,
            answers: m.answers || {},
            photos: m.photos || [],
            rooms: m.rooms || [],
            notes: m.notes || "",
            radiator_photos: m.radiator_photos || [],
            fcaps_photos: m.fcaps_photos || [],
            thermal_photos: m.thermal_photos || [],
            installer_name: m.installer_name || "",
            installer_tm: m.installer_tm || "",
          };
        });
        setMState(ms);
        const applicable = mRes.data.filter((m) => m.active);
        const acts = v.actions || [];
        const extra = [];
        applicable.forEach((m) => {
          const st = ms[m.id];
          if (!st) return;
          (m.questions || []).forEach((q) => {
            if (q.type === "boolean" && st.answers?.[q.label] === false &&
                !acts.some((a) => a.source_measure_id === m.id && a.source_question === q.label)) {
              extra.push({ id: uid(), auto: true, source_measure_id: m.id, source_question: q.label,
                description: `${m.name} — "${q.label}" answered No`, location: m.name, measure_code: m.code,
                assigned_to: "Installer", assigned_person: "", status: "Open", due_date: "",
                photos: [], remediated: false, remediation_photos: [] });
            }
          });
        });
        setActions([...acts, ...extra]);
        setDampEntries(v.damp_mould || []);
        setRemediations(v.remediations || []);
      }
    }
    boot()
      .catch(() => toast.error("Could not load this report — it may be busy. Please try again."))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ensureM = (mid) => mState[mid] || { installed: false, answers: {}, photos: [], rooms: [], notes: "", radiator_photos: [], fcaps_photos: [], thermal_photos: [], installer_name: "", installer_tm: "" };
  const setMeasure = (mid, patch) => setMState((s) => ({ ...s, [mid]: { ...ensureM(mid), ...patch } }));
  const setAnswer = (mid, label, val) =>
    setMState((s) => {
      const cur = s[mid] || { installed: true, answers: {}, photos: [], rooms: [], notes: "" };
      return { ...s, [mid]: { ...cur, answers: { ...cur.answers, [label]: val } } };
    });

  // Rooms
  const addRoom = (mid) => setMeasure(mid, { rooms: [...ensureM(mid).rooms, { id: uid(), type: "", height: "", is_wet_room: false, is_wc_only: false, no_door: false, background_ventilation: "", notes: "", photos: [], height_photos: [], undercut_photos: [], trickle_photos: [], dmev_photos: [], no_sink_photos: [] }] });
  const setRoom = (mid, rid, patch) => setMeasure(mid, { rooms: ensureM(mid).rooms.map((r) => (r.id === rid ? { ...r, ...patch } : r)) });
  const delRoom = (mid, rid) => setMeasure(mid, { rooms: ensureM(mid).rooms.filter((r) => r.id !== rid) });

  // Actions
  const addAction = () => setActions((a) => [...a, { id: uid(), description: "", location: "", measure_code: "", assigned_to: "Installer", assigned_person: "", status: "Open", due_date: "", photos: [], remediated: false, remediation_photos: [] }]);
  const setAction = (aid, patch) => setActions((a) => a.map((x) => (x.id === aid ? { ...x, ...patch } : x)));
  const delAction = (aid) => setActions((a) => a.filter((x) => x.id !== aid));

  // Auto-fault: every measure question answered "No" surfaces as a fault
  const autoFaultFor = (m, q) => ({
    id: uid(), auto: true, source_measure_id: m.id, source_question: q.label,
    description: `${m.name} — "${q.label}" answered No`, location: m.name, measure_code: m.code,
    assigned_to: "Installer", assigned_person: "", status: "Open", due_date: "",
    photos: [], remediated: false, remediation_photos: [],
  });
  const handleAnswer = (m, q, val) => {
    setAnswer(m.id, q.label, val);
    if (q.type !== "boolean") return;
    setActions((a) => {
      const idx = a.findIndex((x) => x.source_measure_id === m.id && x.source_question === q.label);
      if (val === false) return idx >= 0 ? a : [...a, autoFaultFor(m, q)];
      return idx >= 0 ? a.filter((_, i) => i !== idx) : a;
    });
  };
  const markRemediated = (a) => {
    if (!(a.remediation_photos?.length)) { toast.error("Add a photo proving the fault was remediated before marking it complete."); return; }
    setAction(a.id, { remediated: true, status: "Closed", remediated_at: new Date().toISOString() });
    toast.success("Fault marked as remediated.");
  };
  const reopenFault = (aid) => setAction(aid, { remediated: false, status: "Open", remediated_at: "" });

  // Damp & mould findings automatically raise a fault (kept in sync with the findings list)
  useEffect(() => {
    setActions((prev) => {
      const nonDamp = prev.filter((a) => !a.source_damp_id);
      const dampFaults = dampEntries.map((d) => {
        const desc = ["Damp & mould", d.type, d.severity && `${d.severity} severity`].filter(Boolean).join(" — ");
        const base = prev.find((a) => a.source_damp_id === d.id) || {
          id: uid(), assigned_to: "Installer", assigned_person: "", status: "Open",
          due_date: "", photos: [], remediated: false, remediation_photos: [],
        };
        return { ...base, auto: true, source_damp_id: d.id, is_damp: true, measure_code: "DAMP",
                 description: desc, location: d.location || "", photos: d.photos || [] };
      });
      return [...nonDamp, ...dampFaults];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dampEntries]);

  // If the client sign-off satisfaction is left unconfirmed, raise a fault so it doesn't slip through
  useEffect(() => {
    setActions((prev) => {
      const others = prev.filter((a) => a.source !== "client_satisfaction");
      if (form.client_satisfied) return others;
      const base = prev.find((a) => a.source === "client_satisfaction") || {
        id: uid(), assigned_to: "Coordinator", assigned_person: "", status: "Open",
        due_date: "", photos: [], remediated: false, remediation_photos: [],
      };
      return [...others, { ...base, auto: true, source: "client_satisfaction", measure_code: "SIGN-OFF",
                           description: "Resident not confirmed as satisfied with the works", location: "Client sign-off" }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_satisfied]);

  // Damp & mould — simple capture (severity, location, type, photo). Each finding raises a fault.
  const addDamp = () => setDampEntries((d) => [...d, { id: uid(), location: "", severity: "", type: "", notes: "", photos: [] }]);
  const setDamp = (did, patch) => setDampEntries((d) => d.map((x) => (x.id === did ? { ...x, ...patch } : x)));
  const delDamp = (did) => setDampEntries((d) => d.filter((x) => x.id !== did));

  // Defect remediation
  const addRemediation = () => setRemediations((r) => [...r, { id: uid(), defect: "", action_taken: "", location: "", measure_code: "", measure_other: "", before_photos: [], after_photos: [] }]);
  const setRemediation = (rid, patch) => setRemediations((r) => r.map((x) => (x.id === rid ? { ...x, ...patch } : x)));
  const delRemediation = (rid) => setRemediations((r) => r.filter((x) => x.id !== rid));

  // Background ventilation toggle auto-creates a linked fault when "Not sufficient"
  const setBgVent = (mid, room, opt) => {
    setRoom(mid, room.id, { background_ventilation: opt });
    if (opt === "Not sufficient") {
      setActions((a) =>
        a.some((x) => x.room_id === room.id)
          ? a
          : [...a, { id: uid(), auto: true, room_id: room.id, description: `Background ventilation not sufficient — ${room.type || "room"}`, location: room.type || "", measure_code: "VENT", assigned_to: "Installer", status: "Open", photos: [] }]);
    } else {
      setActions((a) => a.filter((x) => !(x.auto && x.room_id === room.id)));
    }
  };

  const findRoomById = (rid) => {
    for (const m of measures) {
      const rr = (ensureM(m.id).rooms || []).find((r) => r.id === rid);
      if (rr) return rr;
    }
    return null;
  };

  const roomPhotoCount = (r) =>
    (r.photos?.length || 0) + (r.height_photos?.length || 0) + (r.undercut_photos?.length || 0) +
    (r.trickle_photos?.length || 0) + (r.dmev_photos?.length || 0) + (r.no_sink_photos?.length || 0);
  const measurePhotoCount = (st) => (st.photos?.length || 0) + (st.radiator_photos?.length || 0) + (st.fcaps_photos?.length || 0) + (st.thermal_photos?.length || 0) + (st.rooms || []).reduce((n, r) => n + roomPhotoCount(r), 0);

  const checks = GENERAL_CHECKS[form.visit_type] || [];
  const appliesToScheme = (m) => !m.schemes || m.schemes.length === 0 || m.schemes.includes(form.client_scheme);
  const visibleMeasures = measures.filter(appliesToScheme);

  const schemeApplies = (m, scheme) => !m.schemes || m.schemes.length === 0 || m.schemes.includes(scheme);
  const hasMeasureData = (st) =>
    !!st && (st.installed || (st.photos?.length || 0) > 0 || (st.rooms?.length || 0) > 0 ||
      (st.answers && Object.keys(st.answers).length > 0) || (st.notes || "").trim().length > 0);

  // Measures that currently hold data but would be hidden by switching to `newScheme`
  const measuresHiddenByScheme = (newScheme) =>
    measures.filter((m) => !schemeApplies(m, newScheme) && hasMeasureData(ensureM(m.id)));

  const handleSchemeChange = (v) => {
    if (v === form.client_scheme) return;
    const hidden = measuresHiddenByScheme(v);
    if (hidden.length > 0) setPendingScheme({ scheme: v, hidden });
    else setField("client_scheme", v);
  };

  // Save every measure that applies to the current scheme OR still holds data (so data is never dropped on a scheme switch)
  const measuresToPersist = measures.filter((m) => appliesToScheme(m) || hasMeasureData(ensureM(m.id)));

  const buildPayload = (status) => ({
    ...form,
    status,
    actions: actions.map((a) => {
      if (a.auto && a.room_id && (!a.photos || a.photos.length === 0)) {
        const rr = findRoomById(a.room_id);
        const ph = rr ? [...(rr.undercut_photos || []), ...(rr.trickle_photos || []), ...(rr.photos || [])] : [];
        return { ...a, photos: ph.slice(0, 3) };
      }
      return a;
    }),
    damp_mould: dampEntries,
    remediations,
    measures: measuresToPersist.map((m) => {
      const st = ensureM(m.id);
      return {
        measure_id: m.id,
        code: m.code,
        name: m.name,
        installed: st.installed,
        answers: st.answers,
        photos: st.photos,
        rooms: st.rooms,
        notes: st.notes,
        radiator_photos: st.radiator_photos || [],
        fcaps_photos: st.fcaps_photos || [],
        thermal_photos: st.thermal_photos || [],
        installer_name: st.installer_name || "",
        installer_tm: st.installer_tm || "",
      };
    }),
  });

  const save = async (status) => {
    if (!form.completed_by || !form.job_address || !form.postcode) {
      toast.error("Please complete: who completed it, job address and postcode.");
      return;
    }
    if (status === "completed") {
      if (!form.front_photo?.id) return toast.error("A front-of-dwelling photo is required to complete (property verification).");
      const badAction = actions.find((a) => a.source !== "client_satisfaction" && (!a.photos || a.photos.length === 0));
      if (badAction) return toast.error("Every fault must have a photo attached to support it. Add a photo to each fault (including those raised from a \u201cNo\u201d answer).");
      const incompleteDamp = dampEntries.find((d) => !d.severity || !d.type || !d.location);
      if (incompleteDamp) return toast.error("Every damp & mould finding needs a severity, type and location.");
      const badDamp = dampEntries.find((d) => !d.photos || d.photos.length === 0);
      if (badDamp) return toast.error("Every damp & mould finding must have a photo attached.");
      const badRemed = remediations.find((r) => (r.after_photos || []).length === 0);
      if (badRemed) return toast.error("Every remediation must include an 'after' photo showing how it was fixed.");
      // Min 5 photos per installed measure
      for (const m of visibleMeasures) {
        const st = ensureM(m.id);
        if (st.installed && measurePhotoCount(st) < MIN_PHOTOS_PER_MEASURE) {
          return toast.error(`${m.name} needs at least ${MIN_PHOTOS_PER_MEASURE} photos (has ${measurePhotoCount(st)}).`);
        }
        // ASHP: one photo per radiator upgraded/replaced
        if (st.installed && m.code === "ASHP") {
          const n = parseInt(st.answers["Number of radiators replaced / upgraded"], 10);
          if (n > 0 && (st.radiator_photos?.length || 0) < n) {
            return toast.error(`ASHP: please add a photo for each radiator — ${n} required, ${st.radiator_photos?.length || 0} added.`);
          }
        }
        // Loft: F-Caps photos required when F-Caps fitted
        if (st.installed && m.code === "B9" && st.answers["F-Caps fitted?"] === true && (st.fcaps_photos?.length || 0) === 0) {
          return toast.error("Loft: a photo of the F-Caps is required when F-Caps are fitted.");
        }
        // CWI: thermal imaging photos required (fill can't be verified visually post-completion)
        if (st.installed && m.code === "B1" && (st.thermal_photos?.length || 0) === 0) {
          return toast.error("Cavity Wall Insulation: at least one thermal imaging photo is required to evidence the cavity fill.");
        }
        // Room-level photo rules
        if (st.installed && m.has_rooms) {
          for (const r of st.rooms) {
            const rn = r.type || "a room";
            if (!r.no_door && (r.undercut_photos?.length || 0) === 0) return toast.error(`${rn}: a door undercut photo is required (or tick 'No door installed').`);
            if ((r.trickle_photos?.length || 0) === 0) return toast.error(`${rn}: a trickle vent photo is required.`);
            if (r.is_wet_room && (r.dmev_photos?.length || 0) === 0) return toast.error(`${rn}: wet rooms require a dMEV / extract photo (or a photo showing none is present).`);
            if (r.is_wc_only && (r.no_sink_photos?.length || 0) === 0) return toast.error(`${rn}: W/C-only rooms require a photo showing no sink is present.`);
          }
        }
      }
    }
    setSaving(true);
    try {
      let floorPlan = form.floor_plan;
      if (floorPlan?.rooms?.length) {
        try {
          const mk = floorPlan.markers || [];
          const [s2, s3] = await Promise.all([
            uploadDataUrl(render2DSnapshot(floorPlan.rooms, { markers: mk }), "floorplan-2d.jpg"),
            uploadDataUrl(render3DSnapshot(floorPlan.rooms, { markers: mk }), "floorplan-3d.jpg"),
          ]);
          floorPlan = { rooms: floorPlan.rooms, markers: mk, snapshot_2d: s2, snapshot_3d: s3 };
        } catch (err) {
          floorPlan = { rooms: floorPlan.rooms, markers: floorPlan.markers || [] };
        }
      }
      const payload = { ...buildPayload(status), floor_plan: floorPlan };
      const res = editing ? await api.put(`/visits/${id}`, payload) : await api.post("/visits", payload);
      toast.success(status === "completed" ? "Visit completed" : "Draft saved");
      navigate(`/visits/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent" />
      </div>
    );

  const title = form.visit_type === "mid" ? "Mid Coordination Visit" : "Post Coordination Visit";

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-24">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Retrofit Coordination · PAS 2035</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      </div>

      {/* Visit type */}
      <Card className="border-slate-200 p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2">
          {[{ k: "mid", l: "Mid Coordination" }, { k: "post", l: "Post Coordination" }].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setField("visit_type", t.k)}
              className={`field-lg rounded-md text-sm font-bold transition-colors ${form.visit_type === t.k ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`type-toggle-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </Card>

      {/* Property verification */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n={<Home className="h-4 w-4" />} title="Property Verification" />
        <p className="mt-1 text-sm text-slate-500">Take a geotagged photo of the front of the dwelling to prove you attended the correct property.</p>
        <div className="mt-4 max-w-xs">
          <PhotoField photos={form.front_photo ? [form.front_photo] : []} onChange={(arr) => setField("front_photo", arr[0] || null)} testId="front-photo-btn" single />
        </div>
      </Card>

      {/* Details */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="1" title="Visit Details" />
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Who completed it *">
            <Input value={form.completed_by} onChange={(e) => setField("completed_by", e.target.value)} className="field-lg" placeholder="Full name" data-testid="field-completed-by" />
          </Field>
          <Field label="Role">
            <Input value={form.completed_by_role} onChange={(e) => setField("completed_by_role", e.target.value)} className="field-lg" data-testid="field-role" />
          </Field>
          <Field label="Client / Scheme">
            <Select value={form.client_scheme} onValueChange={handleSchemeChange}>
              <SelectTrigger className="field-lg" data-testid="field-scheme"><SelectValue /></SelectTrigger>
              <SelectContent>{SCHEMES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Job reference">
            <Input value={form.job_reference} onChange={(e) => setField("job_reference", e.target.value)} className="field-lg" placeholder="e.g. SAF-1042" data-testid="field-jobref" />
          </Field>
          <Field label="Visit date">
            <Input type="date" value={form.visit_date} onChange={(e) => setField("visit_date", e.target.value)} className="field-lg" data-testid="field-date" />
          </Field>
          <Field label="Visit time">
            <Input type="time" value={form.visit_time} onChange={(e) => setField("visit_time", e.target.value)} className="field-lg" data-testid="field-time" />
          </Field>
          <Field label="Postcode *">
            <Input value={form.postcode} onChange={(e) => setField("postcode", e.target.value.toUpperCase())} className="field-lg font-mono uppercase" placeholder="NR15 1AB" data-testid="field-postcode" />
          </Field>
          <Field label="Property type">
            <Select value={form.property_type} onValueChange={(v) => setField("property_type", v)}>
              <SelectTrigger className="field-lg" data-testid="field-property-type"><SelectValue placeholder="Select property type" /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Wall type">
            <Select value={form.wall_type} onValueChange={(v) => setField("wall_type", v)}>
              <SelectTrigger className="field-lg" data-testid="field-wall-type"><SelectValue placeholder="Select wall type" /></SelectTrigger>
              <SelectContent>{WALL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Job address *">
              <Input value={form.job_address} onChange={(e) => setField("job_address", e.target.value)} className="field-lg" placeholder="Property address" data-testid="field-address" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Property location</Label>
            <div className="mt-1.5">
              <PropertyMap lat={form.front_photo?.lat} lng={form.front_photo?.lng} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
            <span className="text-sm font-semibold text-slate-700">Resident present?</span>
            <Switch checked={form.resident_present} onCheckedChange={(v) => setField("resident_present", v)} data-testid="field-resident-present" />
          </div>
          <Field label="Resident name">
            <Input value={form.resident_name} onChange={(e) => setField("resident_name", e.target.value)} className="field-lg" data-testid="field-resident-name" />
          </Field>
        </div>
      </Card>

      {/* General checks */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="2" title={`General ${form.visit_type === "mid" ? "In-Progress" : "Completion"} Checks`} />
        <p className="mt-1 text-sm text-slate-500">Tick each check that passes. When you confirm, you'll be asked to add a short comment for anything left unticked.</p>
        <div className="mt-4 space-y-2">
          {checks.map((c) => {
            const ticked = !!form.general[c];
            return (
              <div key={c}>
                <label className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 transition-colors hover:bg-slate-50 ${form.general_confirmed && !ticked ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid={`gcheck-${c.slice(0, 12)}`}>
                  <Checkbox checked={ticked} onCheckedChange={(v) => setField("general", { ...form.general, [c]: !!v })} className="h-5 w-5" />
                  <span className="text-sm font-medium text-slate-700">{c}</span>
                </label>
                {form.general_confirmed && !ticked && (
                  <Textarea
                    value={form.general_notes[c] || ""}
                    onChange={(e) => setField("general_notes", { ...form.general_notes, [c]: e.target.value })}
                    placeholder="Not ticked — add a comment (e.g. awaiting commissioning certificate, snag outstanding, to be arranged)…"
                    className="mt-1 min-h-[64px] border-amber-200 bg-amber-50/30 text-sm"
                    data-testid={`gcheck-note-${c.slice(0, 12)}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-3">
          {!form.general_confirmed ? (
            <Button type="button" onClick={() => setField("general_confirmed", true)} className="bg-slate-900 font-bold hover:bg-slate-800" data-testid="confirm-checks-btn">
              Confirm checks
            </Button>
          ) : (
            <>
              <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Checks confirmed — {checks.filter((c) => form.general[c]).length}/{checks.length} ticked</span>
              <Button type="button" variant="outline" size="sm" onClick={() => setField("general_confirmed", false)} className="border-slate-300 font-semibold" data-testid="edit-checks-btn">
                Edit checks
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Measures */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="3" title="Measures Installed" />
        <p className="mt-1 text-sm text-slate-500">
          Showing <span className="font-bold text-slate-700">{visibleMeasures.length}</span> measures for <span className="font-bold text-accent">{form.client_scheme}</span>. Tick each measure that applies — questions, photos and rooms appear when selected.
        </p>
        <div className="mt-5 space-y-4">
          {visibleMeasures.map((m) => {
            const st = ensureM(m.id);
            return (
              <div key={m.id} className={`rounded-md border transition-colors ${st.installed ? "border-accent/50 bg-accent/[0.03]" : "border-slate-200"}`}>
                <label className="flex cursor-pointer items-center gap-3 p-4" data-testid={`measure-toggle-${m.code}`}>
                  <Checkbox checked={st.installed} onCheckedChange={(v) => setMeasure(m.id, { installed: !!v })} className="h-5 w-5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-base font-bold text-slate-900">{m.name}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">{m.code}</span>
                    </div>
                    {m.description && <p className="text-xs text-slate-500">{m.description}</p>}
                  </div>
                </label>

                {st.installed && (
                  <div className="space-y-5 border-t border-slate-200 p-4">
                    {/* Installer — autocompletes from the directory for this scheme + measure, auto-fills TrustMark number */}
                    {(() => {
                      const opts = installers.filter((ins) => (ins.measure_codes || []).includes(m.code) && (!ins.schemes?.length || ins.schemes.includes(form.client_scheme)));
                      return (
                        <div className="rounded-md border border-slate-200 bg-white p-4">
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Installer</Label>
                          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <Label className="text-[11px] text-slate-500">Company name</Label>
                              <Input
                                list={`installer-list-${m.code}`}
                                value={st.installer_name || ""}
                                onChange={(e) => {
                                  const name = e.target.value;
                                  const match = opts.find((o) => o.name.toLowerCase() === name.toLowerCase());
                                  setMeasure(m.id, match ? { installer_name: match.name, installer_tm: match.trustmark_number } : { installer_name: name });
                                }}
                                placeholder="Start typing…"
                                className="field-lg mt-1"
                                data-testid={`installer-name-${m.code}`}
                              />
                              <datalist id={`installer-list-${m.code}`}>
                                {opts.map((o) => <option key={o.id} value={o.name}>{`TM ${o.trustmark_number}`}</option>)}
                              </datalist>
                            </div>
                            <div>
                              <Label className="text-[11px] text-slate-500">TrustMark number</Label>
                              <Input
                                value={st.installer_tm || ""}
                                onChange={(e) => setMeasure(m.id, { installer_tm: e.target.value })}
                                placeholder="Auto-fills from directory"
                                className="field-lg mt-1"
                                data-testid={`installer-tm-${m.code}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {(() => {
                      const gate = LOFT_GATES[m.code];
                      const sg = SECTION_GATES[m.code];
                      const renderQ = (q) => {
                        if (gate) {
                          const gv = st.answers[gate.gate];
                          if (gate.loftQuestions.includes(q.label) && gv !== gate.value) return null;
                          if (gate.elsewhereQuestions.includes(q.label) && (gv === gate.value || gv == null || gv === "")) return null;
                        }
                        const onAnswer = (val) => {
                          handleAnswer(m, q, val);
                          if (gate && q.label === gate.gate) {
                            const toClear = val === gate.value ? gate.elsewhereQuestions : gate.loftQuestions;
                            setMState((s) => {
                              const cur = s[m.id] || { installed: true, answers: {}, photos: [], rooms: [], notes: "" };
                              const newAns = { ...cur.answers, [q.label]: val };
                              toClear.forEach((lbl) => delete newAns[lbl]);
                              return { ...s, [m.id]: { ...cur, answers: newAns } };
                            });
                            setActions((acts) => acts.filter((x) => !(x.source_measure_id === m.id && toClear.includes(x.source_question))));
                          }
                          if (sg && q.label === sg.gate && val !== sg.value) {
                            const toClear = m.questions.filter((x) => x.section === sg.section).map((x) => x.label);
                            setMState((s) => {
                              const cur = s[m.id] || { installed: true, answers: {}, photos: [], rooms: [], notes: "" };
                              const newAns = { ...cur.answers, [q.label]: val };
                              toClear.forEach((lbl) => delete newAns[lbl]);
                              return { ...s, [m.id]: { ...cur, answers: newAns } };
                            });
                            setActions((acts) => acts.filter((x) => !(x.source_measure_id === m.id && toClear.includes(x.source_question))));
                          }
                        };
                        return (
                          <div key={q.id}>
                            <Label className="text-sm font-semibold text-slate-700">
                              {q.label} {q.required && <span className="text-destructive">*</span>}
                            </Label>
                            <div className="mt-1.5">
                              <QuestionInput q={q} value={st.answers[q.label]} onChange={onAnswer} testId={`q-${m.code}-${q.id.slice(0, 6)}`} />
                            </div>
                          </div>
                        );
                      };

                      if (!m.questions.some((q) => q.section)) return <div className="space-y-4">{m.questions.map(renderQ)}</div>;

                      const order = [], groups = {}, flat = [];
                      m.questions.forEach((q) => {
                        if (!q.section) { flat.push(q); return; }
                        if (!groups[q.section]) { groups[q.section] = []; order.push(q.section); }
                        groups[q.section].push(q);
                      });
                      return (
                        <div className="space-y-3">
                          {order.map((sec, i) => {
                            if (sg && sec === sg.section && st.answers[sg.gate] !== sg.value) return null;
                            const qs = groups[sec];
                            const answered = qs.filter((q) => st.answers[q.label] != null && st.answers[q.label] !== "").length;
                            return (
                              <details key={sec} open={i === 0 || (sg && sec === sg.section)} className="group rounded-lg border border-slate-200 bg-white" data-testid={`section-${m.code}-${i}`}>
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-lg px-4 py-3 font-heading text-sm font-bold text-slate-800 hover:bg-slate-50">
                                  <span className="flex items-center gap-2">
                                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
                                    {sec}
                                  </span>
                                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${answered >= qs.length ? "bg-accent/15 text-accent" : "bg-slate-100 text-slate-500"}`}>{answered}/{qs.length}</span>
                                </summary>
                                <div className="space-y-4 border-t border-slate-100 px-4 py-4">
                                  {qs.map(renderQ)}
                                </div>
                              </details>
                            );
                          })}
                          {flat.length > 0 && <div className="space-y-4 pt-1">{flat.map(renderQ)}</div>}
                        </div>
                      );
                    })()}

                    {/* ASHP: one photo per radiator upgraded/replaced */}
                    {m.code === "ASHP" && (() => {
                      const n = parseInt(st.answers["Number of radiators replaced / upgraded"], 10) || 0;
                      const have = st.radiator_photos?.length || 0;
                      return (
                        <div className="rounded-md border border-slate-200 bg-white p-4">
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                            Radiator photos (one per radiator)
                            {n > 0 && <span className={`ml-2 ${have >= n ? "text-accent" : "text-amber-600"}`}>{have}/{n}</span>}
                          </Label>
                          <p className="mt-1 text-xs text-slate-500">Add a photo of each radiator replaced / upgraded.</p>
                          <div className="mt-3">
                            <PhotoField photos={st.radiator_photos || []} onChange={(ph) => setMeasure(m.id, { radiator_photos: ph })} testId={`radiator-photo-${m.code}`} />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Loft: F-Caps supporting photos when fitted */}
                    {m.code === "B9" && st.answers["F-Caps fitted?"] === true && (
                      <div className="rounded-md border border-slate-200 bg-white p-4">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">F-Caps photos (required)</Label>
                        <p className="mt-1 text-xs text-slate-500">Upload photos supporting the F-Caps installed.</p>
                        <div className="mt-3">
                          <PhotoField photos={st.fcaps_photos || []} onChange={(ph) => setMeasure(m.id, { fcaps_photos: ph })} testId={`fcaps-photo-${m.code}`} />
                        </div>
                      </div>
                    )}

                    {/* CWI: thermal imaging survey photos (required — hard to verify fill on a post inspection without it) */}
                    {m.code === "B1" && (
                      <div className="rounded-md border border-slate-200 bg-white p-4">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          Thermal imaging photos (required)
                          <span className={`ml-2 ${(st.thermal_photos?.length || 0) > 0 ? "text-accent" : "text-amber-600"}`}>{st.thermal_photos?.length || 0}</span>
                        </Label>
                        <p className="mt-1 text-xs text-slate-500">Cavity fill can't be verified visually on a post inspection — attach thermal images showing even coverage / no cold spots or gaps. Best captured with a temperature difference of ≥10°C inside vs outside.</p>
                        <div className="mt-3">
                          <PhotoField photos={st.thermal_photos || []} onChange={(ph) => setMeasure(m.id, { thermal_photos: ph })} testId={`thermal-photo-${m.code}`} />
                        </div>
                      </div>
                    )}

                    {m.has_rooms && (
                      <div className="rounded-md border border-slate-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                            <DoorOpen className="h-4 w-4" /> Background ventilation — rooms ({st.rooms.length})
                          </Label>
                          <Button type="button" size="sm" variant="outline" onClick={() => addRoom(m.id)} className="border-slate-300 font-bold" data-testid={`add-room-${m.code}`}>
                            <Plus className="mr-1 h-3.5 w-3.5" /> Add room
                          </Button>
                        </div>
                        <div className="mt-3 space-y-3">
                          {st.rooms.map((r) => (
                            <div key={r.id} className="rounded-md border border-slate-200 p-3" data-testid={`room-${r.id}`}>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                  <Label className="text-[11px] font-bold uppercase text-slate-500">Room</Label>
                                  <Select value={r.type} onValueChange={(v) => setRoom(m.id, r.id, { type: v })}>
                                    <SelectTrigger className="field-lg mt-1"><SelectValue placeholder="Room" /></SelectTrigger>
                                    <SelectContent>{ROOM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="flex items-center gap-1 text-[11px] font-bold uppercase text-slate-500"><Ruler className="h-3 w-3" /> Room height (m)</Label>
                                  <Input value={r.height} onChange={(e) => setRoom(m.id, r.id, { height: e.target.value })} placeholder="e.g. 2.4" className="field-lg mt-1" inputMode="decimal" />
                                </div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-4">
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                  <Switch checked={r.is_wet_room} onCheckedChange={(v) => setRoom(m.id, r.id, { is_wet_room: v })} /> Wet room
                                </label>
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                  <Switch checked={r.is_wc_only} onCheckedChange={(v) => setRoom(m.id, r.id, { is_wc_only: v })} /> W/C only
                                </label>
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                  <Switch checked={r.no_door} onCheckedChange={(v) => setRoom(m.id, r.id, { no_door: v })} data-testid={`room-nodoor-${r.id}`} /> No door installed
                                </label>
                              </div>
                              <div className="mt-3">
                                <Label className="text-[11px] font-bold uppercase text-slate-500">Background ventilation</Label>
                                <div className="mt-1.5 flex gap-2">
                                  {["Sufficient", "Not sufficient"].map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => setBgVent(m.id, r, opt)}
                                      className={`field-lg flex-1 rounded-md border text-sm font-bold transition-colors ${
                                        r.background_ventilation === opt
                                          ? opt === "Not sufficient"
                                            ? "border-destructive bg-destructive/10 text-destructive"
                                            : "border-accent bg-accent/10 text-accent"
                                          : "border-slate-300 text-slate-500 hover:bg-slate-50"
                                      }`}
                                      data-testid={`room-bgvent-${r.id}-${opt === "Sufficient" ? "suf" : "not"}`}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                                {r.background_ventilation === "Not sufficient" && (
                                  <p className="mt-1.5 flex items-center gap-1 text-xs font-bold text-destructive">
                                    <AlertTriangle className="h-3.5 w-3.5" /> Insufficient — a fault has been raised to the installer automatically.
                                  </p>
                                )}
                              </div>

                              <LabeledPhotos label="Room height evidence" photos={r.height_photos} onChange={(ph) => setRoom(m.id, r.id, { height_photos: ph })} testId={`room-height-${r.id}`} />
                              {r.no_door ? (
                                <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                                  No door installed — door undercut photo not required for this room.
                                </div>
                              ) : (
                                <LabeledPhotos label="Door undercut" required photos={r.undercut_photos} onChange={(ph) => setRoom(m.id, r.id, { undercut_photos: ph })} testId={`room-undercut-${r.id}`} />
                              )}
                              {r.is_wet_room ? (
                                <>
                                  <div className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
                                    Wet room: trickle vents MUST be removed where a dMEV is installed. Photograph the removed/blanked vent below.
                                  </div>
                                  <LabeledPhotos label="Trickle vents removed (dMEV installed) — evidence" required photos={r.trickle_photos} onChange={(ph) => setRoom(m.id, r.id, { trickle_photos: ph })} testId={`room-trickle-${r.id}`} />
                                  <LabeledPhotos label="dMEV / extract (or photo showing none present)" required photos={r.dmev_photos} onChange={(ph) => setRoom(m.id, r.id, { dmev_photos: ph })} testId={`room-dmev-${r.id}`} />
                                </>
                              ) : (
                                <LabeledPhotos label="Trickle vent" required photos={r.trickle_photos} onChange={(ph) => setRoom(m.id, r.id, { trickle_photos: ph })} testId={`room-trickle-${r.id}`} />
                              )}
                              {r.is_wc_only && (
                                <LabeledPhotos label="Proof no sink present in W/C" required photos={r.no_sink_photos} onChange={(ph) => setRoom(m.id, r.id, { no_sink_photos: ph })} testId={`room-nosink-${r.id}`} />
                              )}
                              <LabeledPhotos label="Other room photos" photos={r.photos} onChange={(ph) => setRoom(m.id, r.id, { photos: ph })} testId={`room-photo-${r.id}`} />

                              <Input value={r.notes} onChange={(e) => setRoom(m.id, r.id, { notes: e.target.value })} placeholder="Room notes" className="field-lg mt-3" />
                              <Button type="button" variant="ghost" size="sm" onClick={() => delRoom(m.id, r.id)} className="mt-2 text-destructive">
                                <Trash2 className="mr-1 h-4 w-4" /> Remove room
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Photos + evidence prompts */}
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Evidence photos (geotagged) — min {MIN_PHOTOS_PER_MEASURE}
                        <span className={`ml-2 ${measurePhotoCount(st) >= MIN_PHOTOS_PER_MEASURE ? "text-accent" : "text-amber-600"}`}>
                          {measurePhotoCount(st)}/{MIN_PHOTOS_PER_MEASURE}
                        </span>
                      </Label>
                      {m.photo_prompts?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {m.photo_prompts.map((p) => (
                            <span key={p} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">{p}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3">
                        <PhotoField photos={st.photos} onChange={(ph) => setMeasure(m.id, { photos: ph })} testId={`photo-upload-${m.code}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Faults / actions */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <SectionTitle n="4" title="Faults & Actions" />
          <Button type="button" onClick={addAction} variant="outline" className="border-slate-300 font-bold" data-testid="add-action-btn">
            <Plus className="mr-2 h-4 w-4" /> Add fault
          </Button>
        </div>
        <p className="mt-1 text-sm text-slate-500">Log any defects / non-conformances. Every measure question answered <strong>No</strong> is added here automatically. Assign each to a responsible <strong>person</strong> and organisation, and attach a remediation photo to close it out.</p>
        <div className="mt-4 space-y-4">
          {actions.length === 0 && <p className="text-sm text-slate-400">No faults logged.</p>}
          {actions.map((a, i) => (
            <div key={a.id} className={`rounded-md border p-4 ${a.remediated ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`} data-testid={`action-${a.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-amber-700">
                <AlertTriangle className="h-4 w-4" /> Fault #{i + 1}
                {a.auto && <Badge variant="outline" className="border-amber-400 text-[10px] text-amber-700">Auto — from “No” answer</Badge>}
                {a.remediated && <Badge className="bg-emerald-600 text-[10px] text-white"><CheckCircle2 className="mr-1 h-3 w-3" /> Remediated</Badge>}
              </div>
              <Textarea value={a.description} onChange={(e) => setAction(a.id, { description: e.target.value })} placeholder="Describe the fault / non-conformance…" className="mt-3 min-h-[70px]" data-testid={`action-desc-${a.id}`} />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Measure / location</Label>
                  <Input value={a.location} onChange={(e) => setAction(a.id, { location: e.target.value })} placeholder="e.g. Loft — north gable" className="field-lg mt-1" />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Organisation</Label>
                  <Input value={a.assigned_to} onChange={(e) => setAction(a.id, { assigned_to: e.target.value })} placeholder="e.g. Dodds Group" className="field-lg mt-1" data-testid={`action-org-${a.id}`} />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Person responsible</Label>
                  <Input value={a.assigned_person || ""} onChange={(e) => setAction(a.id, { assigned_person: e.target.value })} placeholder="e.g. Joe Bloggs" className="field-lg mt-1" data-testid={`action-person-${a.id}`} />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Status</Label>
                  <Select value={a.status} onValueChange={(v) => setAction(a.id, { status: v })}>
                    <SelectTrigger className="field-lg mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-[11px] font-bold uppercase text-slate-500">Photo of the fault{a.source === "client_satisfaction" ? "" : " (required) *"}</Label>
                <div className="mt-2">
                  <PhotoField photos={a.photos} onChange={(ph) => setAction(a.id, { photos: ph })} testId={`action-photo-${a.id}`} />
                </div>
              </div>
              <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
                <Label className="text-[11px] font-bold uppercase text-emerald-700">Remediation evidence (photo) *</Label>
                <p className="mt-0.5 text-xs text-slate-500">A supporting photo is required before this fault can be marked as remediated.</p>
                <div className="mt-2">
                  <PhotoField photos={a.remediation_photos || []} onChange={(ph) => setAction(a.id, { remediation_photos: ph })} testId={`action-remediation-photo-${a.id}`} />
                </div>
                {a.remediated ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> Fault remediated{a.remediated_at ? ` · ${new Date(a.remediated_at).toLocaleDateString("en-GB")}` : ""}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => reopenFault(a.id)} className="border-slate-300 font-bold" data-testid={`reopen-fault-${a.id}`}>
                      Reopen fault
                    </Button>
                  </div>
                ) : (
                  <Button type="button" size="sm" onClick={() => markRemediated(a)} className="mt-3 bg-emerald-600 font-bold text-white hover:bg-emerald-700" data-testid={`remediate-fault-${a.id}`}>
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Fault Remediated
                  </Button>
                )}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => delAction(a.id)} className="mt-2 text-destructive">
                <Trash2 className="mr-1 h-4 w-4" /> Remove fault
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Damp & Mould */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="5" title="Damp & Mould" />
        <p className="mt-1 text-sm text-slate-500">Flag any damp or mould you can see. Record how bad it is, where it is and what type, and take a photo. Each finding is automatically <strong>raised as a fault</strong> for the office to action.</p>

        {/* Floor plan + markers — hidden for now, retained for future re-introduction */}
        {false && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/40 p-4">
          <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><Box className="h-4 w-4" /> Property floor plan &amp; damp/mould map</Label>
          <div className="mt-3">
            <FloorPlanBuilder value={form.floor_plan} onChange={(fp) => setField("floor_plan", fp)} />
          </div>
        </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-slate-800">Findings</h3>
          <Button type="button" onClick={addDamp} variant="outline" className="border-slate-300 font-bold" data-testid="add-damp-btn">
            <Plus className="mr-2 h-4 w-4" /> Add finding
          </Button>
        </div>
        <div className="mt-3 space-y-4">
          {dampEntries.length === 0 && <p className="text-sm text-slate-400">No damp or mould findings recorded.</p>}
          {dampEntries.map((d, i) => (
            <div key={d.id} className="rounded-md border border-orange-300 bg-orange-50/40 p-4" data-testid={`damp-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-orange-700"><Droplets className="h-4 w-4" /> Finding #{i + 1}</div>
                <span className="rounded-full border border-red-300 bg-red-100 px-2.5 py-0.5 text-[11px] font-bold text-red-700">Raised as a fault</span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Severity *</Label>
                  <Select value={d.severity} onValueChange={(v) => setDamp(d.id, { severity: v })}>
                    <SelectTrigger className="field-lg mt-1" data-testid={`damp-severity-${d.id}`}><SelectValue placeholder="Low / Medium / High" /></SelectTrigger>
                    <SelectContent>{DAMP_SEVERITY_LEVELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Type *</Label>
                  <Select value={d.type} onValueChange={(v) => setDamp(d.id, { type: v })}>
                    <SelectTrigger className="field-lg mt-1" data-testid={`damp-type-${d.id}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{DAMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Location *</Label>
                  <Input value={d.location} onChange={(e) => setDamp(d.id, { location: e.target.value })} placeholder="e.g. Bedroom 2, north wall" className="field-lg mt-1" data-testid={`damp-location-${d.id}`} />
                </div>
              </div>

              <div className="mt-3">
                <Label className="text-[11px] font-bold uppercase text-slate-500">Notes (optional)</Label>
                <Textarea value={d.notes} onChange={(e) => setDamp(d.id, { notes: e.target.value })} placeholder="Anything else worth noting…" className="mt-1 min-h-[56px]" />
              </div>

              <div className="mt-3">
                <Label className="text-[11px] font-bold uppercase text-slate-500">Photo (required) *</Label>
                <div className="mt-2">
                  <PhotoField photos={d.photos} onChange={(ph) => setDamp(d.id, { photos: ph })} testId={`damp-photo-${d.id}`} />
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => delDamp(d.id)} className="mt-2 text-destructive">
                <Trash2 className="mr-1 h-4 w-4" /> Remove finding
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Defect Remediation */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <SectionTitle n="6" title="Defect Remediation Works" />
          <Button type="button" onClick={addRemediation} variant="outline" className="border-slate-300 font-bold" data-testid="add-remediation-btn">
            <Plus className="mr-2 h-4 w-4" /> Add remediation
          </Button>
        </div>
        <p className="mt-1 text-sm text-slate-500">Record defects that were corrected — what was wrong and how it was fixed, with before &amp; after photos.</p>
        <div className="mt-4 space-y-4">
          {remediations.length === 0 && <p className="text-sm text-slate-400">No remediation works recorded.</p>}
          {remediations.map((r, i) => (
            <div key={r.id} className="rounded-md border border-emerald-300 bg-emerald-50/40 p-4" data-testid={`remediation-${r.id}`}>
              <div className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                <Wrench className="h-4 w-4" /> Remediation #{i + 1}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Measure</Label>
                  <Select value={r.measure_code} onValueChange={(v) => setRemediation(r.id, { measure_code: v })}>
                    <SelectTrigger className="field-lg mt-1"><SelectValue placeholder="Measure" /></SelectTrigger>
                    <SelectContent>
                      {measures.map((m) => <SelectItem key={m.code} value={m.code}>{m.code} — {m.name}</SelectItem>)}
                      <SelectItem value="Other">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                  {r.measure_code === "Other" && (
                    <Input value={r.measure_other || ""} onChange={(e) => setRemediation(r.id, { measure_other: e.target.value })} placeholder="Describe the defect category" className="field-lg mt-2" data-testid={`remediation-other-${r.id}`} />
                  )}
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Location</Label>
                  <Input value={r.location} onChange={(e) => setRemediation(r.id, { location: e.target.value })} placeholder="e.g. North gable" className="field-lg mt-1" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-[11px] font-bold uppercase text-destructive">Defect — what was wrong</Label>
                <Textarea value={r.defect} onChange={(e) => setRemediation(r.id, { defect: e.target.value })} className="mt-1 min-h-[60px]" data-testid={`remediation-defect-${r.id}`} />
              </div>
              <div className="mt-3">
                <Label className="text-[11px] font-bold uppercase text-accent">Action taken — how it was fixed</Label>
                <Textarea value={r.action_taken} onChange={(e) => setRemediation(r.id, { action_taken: e.target.value })} className="mt-1 min-h-[60px]" data-testid={`remediation-action-${r.id}`} />
              </div>
              <LabeledPhotos label="Before" photos={r.before_photos} onChange={(ph) => setRemediation(r.id, { before_photos: ph })} testId={`remediation-before-${r.id}`} />
              <LabeledPhotos label="After (proof fixed)" required photos={r.after_photos} onChange={(ph) => setRemediation(r.id, { after_photos: ph })} testId={`remediation-after-${r.id}`} />
              <Button type="button" variant="ghost" size="sm" onClick={() => delRemediation(r.id)} className="mt-2 text-destructive">
                <Trash2 className="mr-1 h-4 w-4" /> Remove remediation
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="7" title="Overall Notes & Actions" />
        <Textarea value={form.overall_notes} onChange={(e) => setField("overall_notes", e.target.value)} placeholder="Summary, outstanding snags, required actions, follow-up date…" className="mt-4 min-h-[120px]" data-testid="field-notes" />
      </Card>

      {/* Sign-off */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <SectionTitle n="8" title="Sign-off" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SignaturePad title="Engineer / Coordinator" value={form.engineer_signature} onChange={(s) => setField("engineer_signature", s)} testId="eng-sig" />
          <SignaturePad title="Resident" value={form.resident_signature} onChange={(s) => setField("resident_signature", s)} testId="res-sig" />
        </div>
      </Card>

      {/* Client sign-off */}
      <Card className="border-emerald-200 bg-emerald-50/30 p-6 shadow-sm">
        <SectionTitle n="9" title="Client Sign-off" />
        <p className="mt-1 text-sm text-slate-500">
          {form.visit_type === "post"
            ? "Confirm the client / resident is satisfied with the completed works, capture their feedback, and take their signature."
            : "Confirm the client is happy for the works to proceed, capture their feedback, and take their signature."}
        </p>
        <label className="mt-4 flex items-start gap-3 rounded-md border border-emerald-300 bg-white p-4">
          <Switch checked={form.client_satisfied} onCheckedChange={(v) => setField("client_satisfied", v)} data-testid="client-satisfied-switch" />
          <span className="text-sm font-semibold text-slate-800">
            {form.visit_type === "post"
              ? <>The client confirms they are <strong>satisfied with the completed works</strong>.</>
              : <>The client confirms they are <strong>happy with the works</strong> going ahead.</>}
          </span>
        </label>
        <div className="mt-4">
          <Label className="text-[11px] font-bold uppercase text-slate-500">Client comments / feedback</Label>
          <Textarea value={form.client_comments} onChange={(e) => setField("client_comments", e.target.value)} placeholder="Anything the client would like to add — feedback, requests, concerns…" className="mt-1.5 min-h-[90px]" data-testid="client-comments" />
        </div>
        <div className="mt-4">
          <SignaturePad title="Client signature" value={form.client_signature} onChange={(s) => setField("client_signature", s)} testId="client-sig" />
        </div>
      </Card>

      {/* Sticky actions */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-end gap-3 px-4 py-3 sm:px-6">
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} className="field-lg border-slate-300 font-bold" data-testid="save-draft-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save draft</>}
          </Button>
          <Button onClick={() => save("completed")} disabled={saving} className="field-lg bg-accent font-bold hover:bg-accent/90" data-testid="complete-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-2 h-4 w-4" /> Complete visit</>}
          </Button>
        </div>
      </div>

      <AlertDialog open={!!pendingScheme} onOpenChange={(o) => !o && setPendingScheme(null)}>
        <AlertDialogContent data-testid="scheme-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch scheme to {pendingScheme?.scheme}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  The following {pendingScheme?.hidden.length} measure{pendingScheme?.hidden.length === 1 ? "" : "s"} you've
                  filled in {pendingScheme?.hidden.length === 1 ? "does" : "do"} not apply to <strong>{pendingScheme?.scheme}</strong> and
                  will be hidden from the form:
                </p>
                <ul className="list-disc space-y-0.5 pl-5 font-semibold text-slate-800">
                  {pendingScheme?.hidden.map((m) => (
                    <li key={m.id}>{m.code} — {m.name}</li>
                  ))}
                </ul>
                <p className="rounded-md bg-emerald-50 px-3 py-2 font-semibold text-emerald-700">
                  Your data is preserved and will still be saved — switch back to the original scheme any time to edit it.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scheme-switch-cancel">Keep {form.client_scheme}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setField("client_scheme", pendingScheme.scheme);
                setPendingScheme(null);
              }}
              data-testid="scheme-switch-confirm"
            >
              Switch scheme
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionTitle({ n, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-sm font-bold text-white">{n}</span>
      <h2 className="font-heading text-xl font-bold text-slate-900">{title}</h2>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function LabeledPhotos({ label, photos = [], onChange, testId, required }) {
  return (
    <div className="mt-3">
      <Label className="text-[11px] font-bold uppercase text-slate-500">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="mt-1.5">
        <PhotoField photos={photos} onChange={onChange} testId={testId} />
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange, testId }) {
  if (q.type === "boolean") {
    return (
      <div className="flex gap-2">
        {YNNA.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt === "Yes" ? true : opt === "No" ? false : "N/A")}
            className={`field-lg flex-1 rounded-md border text-sm font-bold transition-colors ${
              (value === true && opt === "Yes") || (value === false && opt === "No") || (value === "N/A" && opt === "N/A")
                ? opt === "No"
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : "border-accent bg-accent/10 text-accent"
                : "border-slate-300 text-slate-500 hover:bg-slate-50"
            }`}
            data-testid={`${testId}-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="min-h-[80px]" data-testid={testId} />;

  const isUnknown = value === "Unknown";
  const withUnknown = (input) =>
    q.allow_unknown ? (
      <div className="flex items-center gap-2">
        <div className="flex-1">{input}</div>
        <button
          type="button"
          onClick={() => onChange(isUnknown ? "" : "Unknown")}
          className={`field-lg shrink-0 rounded-md border px-3 text-sm font-bold transition-colors ${isUnknown ? "border-slate-500 bg-slate-100 text-slate-700" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}
          data-testid={`${testId}-unknown`}
        >
          Unknown
        </button>
      </div>
    ) : input;

  if (q.type === "number")
    return withUnknown(
      <Input type="number" value={isUnknown ? "" : (value ?? "")} disabled={isUnknown} placeholder={isUnknown ? "Unknown" : ""} onChange={(e) => onChange(e.target.value)} className="field-lg w-full" data-testid={testId} />
    );
  if (q.type === "select")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="field-lg" data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  return withUnknown(
    <Input value={isUnknown ? "" : (value || "")} disabled={isUnknown} placeholder={isUnknown ? "Unknown" : ""} onChange={(e) => onChange(e.target.value)} className="field-lg w-full" data-testid={testId} />
  );
}
